import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronDown,
  FileText,
  Clock,
} from "lucide-react"
import * as api from "../api/apcm-ops.api"
import type { CarePlanGenerationStatus } from "../api/apcm-ops.api"
import { cn } from "@/lib/utils"
import { timestampMs } from "@/lib/datetime"

interface CarePlanGenerationTrackerProps {
  orgId: string
}

function timeSince(iso: string | null): string {
  if (!iso) return ""
  const diff = Date.now() - timestampMs(iso)
  if (Number.isNaN(diff)) return ""
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  return `${hrs}h ${mins % 60}m ago`
}

export function CarePlanGenerationTracker({ orgId }: CarePlanGenerationTrackerProps) {
  const [status, setStatus] = useState<CarePlanGenerationStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [generatingOpen, setGeneratingOpen] = useState(false)
  const [failedOpen, setFailedOpen] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    let mounted = true

    const fetchStatus = async () => {
      try {
        const data = await api.getCarePlanGenerationStatus(orgId)
        if (mounted) {
          setStatus(data)
          setLoading(false)
        }
      } catch {
        if (mounted) setLoading(false)
      }
    }

    fetchStatus()

    // Poll every 5 seconds
    pollRef.current = setInterval(fetchStatus, 5000)

    return () => {
      mounted = false
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [orgId])

  // Stop polling when no longer active
  useEffect(() => {
    if (status && !status.is_active && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [status])

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (!status || status.total === 0) {
    return null // Don't show card if no care plan activity
  }

  const total = status.total
  const done = status.completed + status.failed
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  const sqsTotal = status.sqs.messages_available + status.sqs.messages_in_flight + status.sqs.messages_delayed

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Care Plan Generation
        </CardTitle>
        {status.is_active ? (
          <Badge variant="outline" className="text-info border-info/30 gap-1.5">
            <Loader2 className="h-3 w-3 animate-spin" />
            In Progress
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className={cn(
              status.failed > 0
                ? "text-warning border-warning/30"
                : "text-success border-success/30"
            )}
          >
            Complete
          </Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {done} of {total} patients processed
            </span>
            <span className="font-medium">{pct}%</span>
          </div>
          <div className="h-2.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full flex">
              {status.completed > 0 && (
                <div
                  className="bg-success transition-all duration-700"
                  style={{ width: `${(status.completed / total) * 100}%` }}
                />
              )}
              {status.failed > 0 && (
                <div
                  className="bg-destructive transition-all duration-700"
                  style={{ width: `${(status.failed / total) * 100}%` }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border bg-info/10 border-info/30 p-3 text-center">
            <p className="text-2xl font-semibold text-info">{status.generating}</p>
            <p className="text-xs text-info mt-0.5">Generating</p>
          </div>
          <div className="rounded-lg border bg-success/10 border-success/30 p-3 text-center">
            <p className="text-2xl font-semibold text-success">{status.completed}</p>
            <p className="text-xs text-success mt-0.5">Completed</p>
          </div>
          <div className={cn(
            "rounded-lg border p-3 text-center",
            status.failed > 0
              ? "bg-destructive/10 border-destructive/30"
              : "bg-muted/30 border-muted"
          )}>
            <p className={cn(
              "text-2xl font-semibold",
              status.failed > 0 ? "text-destructive" : "text-muted-foreground"
            )}>
              {status.failed}
            </p>
            <p className={cn(
              "text-xs mt-0.5",
              status.failed > 0 ? "text-destructive" : "text-muted-foreground"
            )}>
              Failed
            </p>
          </div>
        </div>

        {/* SQS Queue info */}
        {status.is_active && sqsTotal > 0 && (
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
              Queue Status
            </p>
            <div className="flex gap-4 text-muted-foreground">
              <span>{status.sqs.messages_delayed} queued</span>
              <span>{status.sqs.messages_in_flight} processing</span>
              <span>{status.sqs.messages_available} ready</span>
            </div>
          </div>
        )}

        {/* Per-state breakdown */}
        {Object.keys(status.state_counts).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(status.state_counts)
              .sort(([, a], [, b]) => b - a)
              .map(([state, count]) => (
                <Badge key={state} variant="outline" className="text-xs">
                  {state}: {count}
                </Badge>
              ))}
          </div>
        )}

        {/* Currently generating patients */}
        {status.patients_generating.length > 0 && (
          <Collapsible open={generatingOpen} onOpenChange={setGeneratingOpen}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <Loader2 className="h-3 w-3 animate-spin" />
                {status.patients_generating.length} patients currently generating
                <ChevronDown className={cn("h-3 w-3 transition-transform", generatingOpen && "rotate-180")} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                {status.patients_generating.map((p) => (
                  <div
                    key={p.patient_id}
                    className="rounded border bg-info/10 border-info/30 p-2 text-xs flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin text-info" />
                      <span className="font-medium">{p.patient_name}</span>
                    </div>
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {timeSince(p.since)}
                    </span>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Failed patients */}
        {status.patients_failed.length > 0 && (
          <Collapsible open={failedOpen} onOpenChange={setFailedOpen}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-1.5 text-xs text-destructive hover:text-destructive transition-colors">
                <XCircle className="h-3 w-3" />
                {status.patients_failed.length} patients failed
                <ChevronDown className={cn("h-3 w-3 transition-transform", failedOpen && "rotate-180")} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                {status.patients_failed.map((p) => (
                  <div
                    key={p.patient_id}
                    className="rounded border bg-destructive/10 border-destructive/30 p-2 text-xs flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <XCircle className="h-3 w-3 text-destructive" />
                      <span className="font-medium">{p.patient_name}</span>
                    </div>
                    <span className="text-muted-foreground">{timeSince(p.since)}</span>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  )
}
